// utils/format.ts — Helpers de formatage purs (dates, labels, fallbacks).
//
// Premier fichier .ts strict du frontend (S1 du plan migration TS+Vite).
// Aucune dépendance externe, aucun accès DOM, aucun fetch — testable
// 100 % isolément via Vitest.
//
// État S1 (2026-05-10) : ces fonctions sont DUPLIQUÉES dans core.js qui
// reste source of truth runtime jusqu'à la bascule FastAPI vers dist/
// en S6. Ce fichier sert :
//   1. de référence typée pour les futurs imports nommés (S2-S5)
//   2. de filet de tests Vitest (S1)
//   3. de remplacement direct des fonctions de core.js en S6
//
// Quand core.js sera converti en .ts en S2-S3, importer depuis ici.

/**
 * Date du jour au format ISO court (YYYY-MM-DD), basé sur l'horloge locale.
 * Utilisé par les défauts de date dans les formulaires.
 */
export function todayStr(): string {
  // toISOString() renvoie en UTC : pour la convention "date du jour vue
  // par l'utilisateur", on doit prendre la date locale, pas UTC.
  // Ex. à 23h Paris (UTC 22h), on veut la date du jour Paris, pas UTC.
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format une date ISO YYYY-MM-DD en DD/MM/YYYY (convention française).
 * Renvoie '—' si la valeur est null/undefined/vide.
 * Renvoie la chaîne d'entrée inchangée si elle ne matche pas le format ISO.
 */
export function fmtDate(s: string | null | undefined): string {
  if (!s) return '—';
  // yyyy-mm-dd → dd/mm/yyyy
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
}

/**
 * Libellé d'affichage d'une priorité numérique (0=très haute → 4=très basse).
 * Renvoie '—' pour toute valeur hors plage.
 */
export function priorityLabel(p: number | null | undefined): string {
  const labels = ['0 - Très haute', '1 - Haute', '2 - Normale', '3 - Basse', '4 - Très basse'];
  if (p == null) return '—';
  return labels[p] ?? '—';
}

/**
 * Représentation minimale d'un risque pour le calcul de son code d'affichage.
 * Le code peut venir de différentes propriétés selon la source (API, formulaire,
 * chaîne directe).
 */
export interface RiskLike {
  id?: number | null;
  risk_id?: number | null;
  code?: string | null;
  risk_code?: string | null;
}

/**
 * Code d'un risque : valeur saisie OU fallback "R<id>" quand le code est vide.
 *
 * Règle UX globale : un risque doit toujours afficher un identifiant court
 * à l'écran. Si l'auteur n'a pas saisi de code, on tombe sur "R<id>" qui
 * reste stable et permet de référencer le risque sans ambiguïté.
 *
 * Accepte une chaîne directe (code déjà connu) ou un objet partiel.
 * Renvoie '?' si rien d'identifiable.
 */
export function riskCodeOrFallback(risk: string | RiskLike | null | undefined): string {
  if (!risk) return '?';
  if (typeof risk === 'string') return risk;

  const c = risk.code || risk.risk_code || null;
  if (c) return c;

  const id = risk.id ?? risk.risk_id ?? null;
  return id ? `R${id}` : '?';
}
